import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

export function NotFoundPage() {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Entrance animation
    const container = containerRef.current
    if (container) {
      container.style.opacity = '0'
      container.style.transform = 'translateY(20px)'
      requestAnimationFrame(() => {
        container.style.transition = 'opacity 0.8s ease-out, transform 0.8s ease-out'
        container.style.opacity = '1'
        container.style.transform = 'translateY(0)'
      })
    }
  }, [])

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Cooler radial glow - slightly desaturated to convey "lost" */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 40%, rgba(179, 58, 43, 0.10) 0%, transparent 70%)',
        }}
      />

      {/* Keyframe animations */}
      <style>{`
        @keyframes float-drift {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-80px) translateX(20px) scale(0.5); opacity: 0; }
        }
        @keyframes pulse-glow-dim {
          0%, 100% { box-shadow: 0 0 30px 8px rgba(179, 58, 43, 0.2); }
          50% { box-shadow: 0 0 50px 15px rgba(179, 58, 43, 0.35); }
        }
        @keyframes fragment-drift-1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-3px, -4px) rotate(-5deg); }
        }
        @keyframes fragment-drift-2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(4px, -3px) rotate(8deg); }
        }
        @keyframes fragment-drift-3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-2px, 5px) rotate(-3deg); }
        }
        @keyframes fragment-drift-4 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(3px, 3px) rotate(6deg); }
        }
      `}</style>

      {/* Drifting particles - scattered pattern */}
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 rounded-full bg-white/40"
          style={{
            left: `${20 + i * 9}%`,
            bottom: `${25 + (i % 3) * 10}%`,
            animation: `float-drift ${4 + i * 0.4}s ease-out infinite`,
            animationDelay: `${i * 0.5}s`,
          }}
        />
      ))}

      <div ref={containerRef} className="relative z-10 text-center max-w-md">
        {/* 404 Icon - Scattered fragments representing a broken/missing page */}
        <div className="relative mx-auto w-24 h-24 mb-8">
          {/* Glow ring - dimmer for "lost" feel */}
          <div
            className="absolute inset-0 rounded-full"
            style={{ animation: 'pulse-glow-dim 3s ease-in-out infinite' }}
          />
          {/* Icon container with scattered geometric fragments */}
          <div className="relative w-full h-full rounded-full bg-bg-surface border border-white/10 flex items-center justify-center">
            <svg
              className="w-12 h-12"
              viewBox="0 0 48 48"
              fill="none"
            >
              {/* Fragment 1 - Top left piece */}
              <path
                d="M16 14L22 18L18 24L12 20Z"
                fill="rgba(179, 58, 43, 0.6)"
                stroke="rgba(179, 58, 43, 0.9)"
                strokeWidth="1"
                style={{ animation: 'fragment-drift-1 4s ease-in-out infinite' }}
              />
              {/* Fragment 2 - Top right piece */}
              <path
                d="M26 12L34 16L30 22L24 18Z"
                fill="rgba(179, 58, 43, 0.5)"
                stroke="rgba(179, 58, 43, 0.8)"
                strokeWidth="1"
                style={{ animation: 'fragment-drift-2 3.5s ease-in-out infinite', animationDelay: '0.2s' }}
              />
              {/* Fragment 3 - Bottom left piece */}
              <path
                d="M14 26L20 28L18 36L10 32Z"
                fill="rgba(179, 58, 43, 0.4)"
                stroke="rgba(179, 58, 43, 0.7)"
                strokeWidth="1"
                style={{ animation: 'fragment-drift-3 4.5s ease-in-out infinite', animationDelay: '0.4s' }}
              />
              {/* Fragment 4 - Bottom right piece */}
              <path
                d="M28 26L36 28L34 36L26 34Z"
                fill="rgba(179, 58, 43, 0.55)"
                stroke="rgba(179, 58, 43, 0.85)"
                strokeWidth="1"
                style={{ animation: 'fragment-drift-4 3.8s ease-in-out infinite', animationDelay: '0.3s' }}
              />
              {/* Center dot - the "origin" from which pieces scattered */}
              <circle
                cx="24"
                cy="24"
                r="2"
                fill="rgba(179, 58, 43, 0.8)"
              />
            </svg>
          </div>
        </div>

        {/* Heading - Large 404 */}
        <h1 className="font-heading-en text-[4rem] leading-none text-white/90 mb-2 tracking-tight">
          404
        </h1>

        {/* Subtitle */}
        <p className="font-heading-en text-h2 text-white/70 mb-4">
          {t('notFound.title', 'Page Not Found')}
        </p>

        {/* Subtext */}
        <p className="font-body-ja text-body text-muted-foreground mb-8 leading-relaxed">
          {t('notFound.message', 'The page you\'re looking for doesn\'t exist or has been moved.')}
        </p>

        {/* Go Home button */}
        <Button
          variant="outline"
          size="lg"
          asChild
          className="min-w-[160px] border-white/10 hover:bg-white/5 hover:border-white/20 transition-all"
        >
          <Link to="/">
            {t('notFound.goHome', 'Go Home')}
          </Link>
        </Button>
      </div>
    </div>
  )
}
